import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Error } from '@components/error/src/error';
import { Input } from '@components/input';
import { InputCheckbox } from '@components/input-checkbox/src';
import { LabelPrimary } from '@components/label';
import { SectionDisclosure } from '@components/section-disclosure';
import { Select, SelectOption } from '@components/select';
import { ToggleSection } from '@components/toggle-section';
import { TOGGLE_SECTION_SIZE_L, TOGGLE_SECTION_SIZE_S } from '@components/toggle-section/src/toggle-section.type';
import { SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';
import classNames from 'classnames';

import {
  RegulationSettingsSectionProps,
  SIAE_STATUS_APPROVED,
  SIAE_STATUS_EMPTY,
  SIAE_STATUS_EXCLUDED,
  SiaeStatus,
} from './regulation-settings-section.type';

export const REGULATION_SETTINGS_SECTION_TEST_ID = 'regulation-settings-section';

export const RegulationSettingsSection = ({
  value,
  onChange,
  open,
  onToggleOpen,
  disabled = false,
  isFreeOffer = false,
  isSiaeApprovalAllowed = true,
  errors,
  dataTestId = REGULATION_SETTINGS_SECTION_TEST_ID,
}: RegulationSettingsSectionProps) => {
  const { t } = useTranslation();
  const isMobileViewport = useWindowSize(SM_VIEWPORT);
  const toggleSize = isMobileViewport ? TOGGLE_SECTION_SIZE_S : TOGGLE_SECTION_SIZE_L;

  const hasCartaCultura = value.allows18App !== undefined;
  const hasNeedsUserVerified = value.needsUserVerified !== undefined;
  const hasSiaeStatus = value.siaeStatus !== undefined;
  const hasAbbonamentoId = value.abbonamentoId !== undefined;
  const hasSuperAdminFields = hasSiaeStatus || hasAbbonamentoId;
  const hasExcludeFromC1 = value.excludeFromC1 !== undefined;

  const siaeStatusHasError = !!errors?.siaeStatus;
  const abbonamentoIdHasError = !!errors?.abbonamentoId;

  const siaeStatusId = `${dataTestId}-siae-status`;
  const siaeStatusErrorId = `${siaeStatusId}-error`;
  const abbonamentoIdFieldId = `${dataTestId}-abbonamento-id`;
  const abbonamentoIdErrorId = `${abbonamentoIdFieldId}-error`;
  const excludeFromC1Name = `${dataTestId}-exclude-from-c1`;

  const handleSiaeStatusChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ siaeStatus: event.currentTarget.value as SiaeStatus });
  };

  const handleAbbonamentoIdChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ abbonamentoId: event.currentTarget.value });
  };

  return (
    <SectionDisclosure
      title={t('offers:admissionRegulationSettingsSectionTitle')}
      open={open}
      onToggleOpen={onToggleOpen}
      dataTestId={`${dataTestId}-disclosure`}>
      <section
        data-testid={dataTestId}
        className={classNames('flex flex-col gap-6 w-full py-6', {
          'cursor-not-allowed opacity-50 pointer-events-none': disabled,
        })}>
        {hasCartaCultura && (
          <div className='flex flex-col gap-2 w-full'>
            <ToggleSection
              value={!!value.allows18App}
              onChange={(next) => onChange({ allows18App: next })}
              label={t('offers:allows18AppTitle')}
              subLabel={t('offers:allows18AppSubtitle')}
              size={toggleSize}
              disabled={disabled || isFreeOffer}
              className='py-0!'
              dataTestId={`${dataTestId}-carta-cultura`}
            />
            {errors?.allows18App && (
              <Error id={`${dataTestId}-carta-cultura-error`} dataTestId={`${dataTestId}-carta-cultura-error`}>
                {errors.allows18App}
              </Error>
            )}
          </div>
        )}

        {hasNeedsUserVerified && (
          <ToggleSection
            value={!!value.needsUserVerified}
            onChange={(next) => onChange({ needsUserVerified: next })}
            label={t('offers:needsUserVerifiedTitle')}
            subLabel={t('offers:needsUserVerifiedSubtitle')}
            size={toggleSize}
            disabled={disabled}
            className='py-0!'
            dataTestId={`${dataTestId}-needs-user-verified`}
          />
        )}

        {hasSuperAdminFields && (
          <div className='flex flex-col md:flex-row md:items-start gap-6 w-full'>
            {hasSiaeStatus && (
              <div className='flex flex-col gap-2 flex-1 min-w-0'>
                <LabelPrimary
                  htmlFor={siaeStatusId}
                  hasError={siaeStatusHasError}
                  marginBottomClassName=''
                  customColors='text-secondary'>
                  {t('offers:siaeStatusLabel')}
                </LabelPrimary>
                <Select
                  id={siaeStatusId}
                  name='siaeStatus'
                  value={value.siaeStatus ?? SIAE_STATUS_EMPTY}
                  onChange={handleSiaeStatusChange}
                  disabled={disabled}
                  readOnly={disabled}
                  hasError={siaeStatusHasError}
                  aria-invalid={siaeStatusHasError}
                  aria-describedby={siaeStatusHasError ? siaeStatusErrorId : undefined}
                  data-testid={`${dataTestId}-siae-status-select`}>
                  <SelectOption value={SIAE_STATUS_EMPTY} label={t('offers:siaeStatusPending')} />
                  <SelectOption
                    value={SIAE_STATUS_APPROVED}
                    label={t('offers:siaeStatusApproved')}
                    disabled={isFreeOffer || !isSiaeApprovalAllowed}
                  />
                  <SelectOption value={SIAE_STATUS_EXCLUDED} label={t('offers:siaeStatusExcluded')} />
                </Select>
                {errors?.siaeStatus && (
                  <Error id={siaeStatusErrorId} dataTestId={siaeStatusErrorId}>
                    {errors.siaeStatus}
                  </Error>
                )}
              </div>
            )}

            {hasAbbonamentoId && (
              <div className='flex flex-col gap-2 flex-1 min-w-0'>
                <LabelPrimary
                  htmlFor={abbonamentoIdFieldId}
                  hasError={abbonamentoIdHasError}
                  marginBottomClassName=''
                  customColors='text-secondary'>
                  {t('offers:abbonamentoIdLabel')}
                </LabelPrimary>
                <Input
                  id={abbonamentoIdFieldId}
                  name='abbonamentoId'
                  type='text'
                  placeholder={t('offers:abbonamentoIdPlaceholder')}
                  value={value.abbonamentoId ?? ''}
                  onChange={handleAbbonamentoIdChange}
                  disabled={disabled}
                  readOnly={disabled}
                  hasError={abbonamentoIdHasError}
                  aria-invalid={abbonamentoIdHasError}
                  aria-describedby={abbonamentoIdHasError ? abbonamentoIdErrorId : undefined}
                  data-testid={`${dataTestId}-abbonamento-id-input`}
                />
                {errors?.abbonamentoId && (
                  <Error id={abbonamentoIdErrorId} dataTestId={abbonamentoIdErrorId}>
                    {errors.abbonamentoId}
                  </Error>
                )}
              </div>
            )}
          </div>
        )}

        {hasExcludeFromC1 && (
          <div className='flex flex-col gap-4 w-full' data-testid={`${dataTestId}-exclude-from-c1`}>
            <p id={`${excludeFromC1Name}-title`} className='text-body-xs text-secondary'>
              {t('offers:excludeSiaeCheckboxTitle')}
            </p>
            <label htmlFor={excludeFromC1Name} className='flex items-start gap-3 w-full cursor-pointer'>
              <InputCheckbox
                name={excludeFromC1Name}
                value={!!value.excludeFromC1}
                disabled={disabled}
                onChange={(next) => onChange({ excludeFromC1: next })}
                aria-labelledby={`${excludeFromC1Name}-title ${excludeFromC1Name}-label`}
                data-testid={`${dataTestId}-exclude-from-c1-checkbox`}
              />
              <span id={`${excludeFromC1Name}-label`} className='text-body-s text-secondary'>
                {t('offers:excludeSiaeCheckboxSubtitle')}
              </span>
            </label>
          </div>
        )}
      </section>
    </SectionDisclosure>
  );
};
